/**
 * /accounts/:accountId/statement — per-account, per-month bank-statement
 * view. Thin shell: pulls everything from `useStatement` and composes
 * the header, entries table, and totals footer.
 *
 * Split from the original 392-line single-file implementation per the
 * pattern in components/CONTRIBUTING.md.
 *
 *   pages/Statement.js                 ← this shell, <90 lines
 *   pages/statement/
 *     useStatement.js                  ← all useState/useEffect/handlers
 *     StatementHeader.jsx              ← header card + month/year + ExportMenu
 *     EntriesTable.jsx                 ← entries table + empty state
 *     TotalsFooter.jsx                 ← credits / debits / closing
 *     constants.js                     ← MONTHS + formatDateUK
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { FADE_IN_UP_HIDDEN, FADE_IN_UP_SHOWN } from '../lib/animations';
import { getAccountIcon } from '../lib/accountIcons';
import { useStatement } from './statement/useStatement';
import StatementHeader from './statement/StatementHeader';
import EntriesTable from './statement/EntriesTable';
import TotalsFooter from './statement/TotalsFooter';
import { MONTHS } from './statement/constants';

const Statement = () => {
  const {
    loading, atCap, tierHorizonMonths, tierLabel,
    year, month, statement, accountTypes, yearOptions, monthOptions,
    setYear, setMonth, stepMonth,
  } = useStatement();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052FF]"></div>
      </div>
    );
  }

  if (!statement) {
    return (
      <div className="p-6 sm:p-8">
        <Link to="/accounts" className="text-sm text-[#0052FF] inline-flex items-center gap-1 hover:underline">
          <ArrowLeft className="w-4 h-4" />
          Back to Accounts
        </Link>
        <p className="mt-6 text-[#64748B]">Statement unavailable.</p>
      </div>
    );
  }

  const { account, period, opening_balance, closing_balance, money_in, money_out, entries } = statement;
  const type = accountTypes.find((t) => t.slug === account.account_type);
  const Icon = getAccountIcon(type?.icon);
  const isLiability = (account.kind || type?.kind) === 'liability';
  const monthLabel = MONTHS.find((m) => m.value === period.month)?.label || '';
  const todayIso = new Date().toISOString().split('T')[0];

  // For liability accounts, the "credit" column shows balance reductions
  // (payments toward debt) and the "debit" column shows balance increases
  // (new charges/drawdowns). Internally those are stored as expense and
  // income respectively (see transfer_utils.transfer_leg_types).
  const showAsCredit = (tx_type) => (isLiability ? tx_type === 'expense' : tx_type === 'income');

  return (
    <div className="p-6 sm:p-8" data-testid="statement-page">
      <Link to="/accounts" className="text-sm text-[#0052FF] inline-flex items-center gap-1 hover:underline print-hide" data-testid="back-to-accounts">
        <ArrowLeft className="w-4 h-4" />
        Back to Accounts
      </Link>

      <motion.div initial={FADE_IN_UP_HIDDEN} animate={FADE_IN_UP_SHOWN} className="mt-4 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <StatementHeader
          account={account}
          type={type}
          Icon={Icon}
          isLiability={isLiability}
          period={period}
          monthLabel={monthLabel}
          year={year}
          month={month}
          setYear={setYear}
          setMonth={setMonth}
          stepMonth={stepMonth}
          yearOptions={yearOptions}
          monthOptions={monthOptions}
          atCap={atCap}
          tierHorizonMonths={tierHorizonMonths}
          tierLabel={tierLabel}
          opening_balance={opening_balance}
          entries={entries}
          todayIso={todayIso}
          showAsCredit={showAsCredit}
        />
        <EntriesTable
          entries={entries}
          opening_balance={opening_balance}
          todayIso={todayIso}
          showAsCredit={showAsCredit}
        />
        <TotalsFooter
          isLiability={isLiability}
          money_in={money_in}
          money_out={money_out}
          closing_balance={closing_balance}
        />
      </motion.div>

      <p className="text-xs text-[#64748B] text-center mt-6">
        Statement for {monthLabel} {period.year} · Items tagged <span className="font-medium">Scheduled</span> are projections from your recurring transactions. Confirm them on the Reconcile page when they actually occur.
      </p>
    </div>
  );
};

export default Statement;
